import { useCallback } from 'react';

/**
 * Define la estructura esperada de los datos del archivo que vienen de la API.
 */
interface FilePayload {
  file: Blob;
  name?: string; // Nombre opcional que puede venir del backend
}

/**
 * Hook personalizado para encapsular la lógica de descarga de archivos.
 * @returns Un objeto con la función `downloadFile`.
 */
export const useFileDownloader = () => {
  const downloadFile = useCallback((filePayload: FilePayload, fileName: string) => {
    // 1. Verificar que recibimos datos válidos del archivo
    if (!filePayload || !filePayload.file || !(filePayload.file instanceof Blob)) {
      // Lanzamos un error para que el componente que lo usa pueda manejarlo (ej. mostrar una notificación)
      throw new Error('No se recibió un archivo válido del servidor');
    }

    // 2. Crear una URL temporal para el archivo recibido (Blob)
    const fileURL = window.URL.createObjectURL(filePayload.file);

    // 3. Crear un enlace <a> en memoria
    const link = document.createElement('a');
    link.href = fileURL;
    link.setAttribute('download', fileName); // Usamos el nombre de archivo dinámico que nos pasaron

    // 4. Simular un clic en el enlace para iniciar la descarga
    document.body.appendChild(link);
    link.click();
    link.remove(); // Limpiar el enlace del DOM inmediatamente después del clic

    // 5. Liberar la URL del objeto para evitar memory leaks
    setTimeout(() => window.URL.revokeObjectURL(fileURL), 100);
  }, []);

  return { downloadFile };
};